import { createFileRoute, useSearch } from "@tanstack/react-router";
import { z } from "zod";

import { SiteFooter } from "@/components/site/SiteFooter";
import { PilotFormSection } from "@/components/site/sections";
import { FaqSection, faqJsonLd } from "@/components/site/FaqSection";
import { pilotFaqs } from "@/content/faqs";

const joinSearchSchema = z.object({
  type: z.enum(["learner", "parent", "school", "general", "early-access"]).optional(),
});

export const Route = createFileRoute("/join-the-pilot")({
  validateSearch: joinSearchSchema,
  head: () => ({
    meta: [
      { title: "Join the Pilot — adaptED" },
      {
        name: "description",
        content: "Sign up to join the adaptED pilot programme as a learner, parent or teacher.",
      },
      { property: "og:title", content: "Join the Pilot — adaptED" },
      { property: "og:description", content: "Apply to take part in the adaptED pilot programme." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.adapted.info/join-the-pilot" },
      { property: "og:site_name", content: "adaptED" },
      { name: "twitter:title", content: "Join the Pilot — adaptED" },
      { name: "twitter:description", content: "Apply to take part in the adaptED pilot programme." },
    ],
    links: [{ rel: "canonical", href: "https://www.adapted.info/join-the-pilot" }],
    scripts: [faqJsonLd(pilotFaqs, "https://www.adapted.info/join-the-pilot")],
  }),
  component: JoinThePilotPage,
});

function JoinThePilotPage() {
  const search = useSearch({ from: "/join-the-pilot" });
  const type = search.type ?? "general";
  const titles: Record<"learner" | "parent" | "school" | "general" | "early-access", string> = {
    learner: "Nominate Your School",
    parent: "Nominate Your Child's School",
    school: "Register Your School's Interest",
    general: "Join the Pilot",
    "early-access": "Get Early Access",
  };
  const heading = titles[type];

  return (
    <main id="top">
      <PilotFormSection heading={heading} defaultType={type} headingAs="h1" />
      <FaqSection
        items={pilotFaqs}
        heading="Pilot programme FAQs"
        paragraph="What joining the adaptED pilot involves, and what happens next."
      />
      <SiteFooter />
    </main>
  );
}
